import DrawerTutorialButton from "components/DrawerTutorial/DrawerTutorialButton/DrawerTutorialButton";
import FieldAction from "components/FieldAction/FieldAction";
import { FieldInstance, UpdateObj, User, UserForResolver } from "db/definitions";
import { Colors } from "@factorearth/component-library/dist/Theme/types";
import useSwipe from "hooks/useSwipe";
import React, { useEffect, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiList, FiPlus } from "react-icons/fi";
import paginateCarousel from "utils/paginateCarousel";
import { UpdateContext } from "utils/UpdateContext";
import CardScroll from "./CardScroll/CardScroll";
import styles from "./CardView.module.scss";
import { Button } from "@factorearth/component-library";

interface Props {
	color: Colors;
	fieldsOrder: string[];
	initial: string;
	online: boolean;
	rowsData: FieldInstance[][];
	setCardView: (newCardView: string | null) => void;
	setupDrawer: (location: string) => void;
	userDoc: UserForResolver;
	handleAddManyRows: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => Promise<{ newRowKey: string; newRowData: FieldInstance[] } | undefined>;
	rowLoading: boolean;
}

// shyeah brah - fieldsOrder must be up to date here, we grab rows by index from the fields order.  Then changing
// the card switches up the index.
function CardView(props: Props) {
	const { color, fieldsOrder, initial, online, rowsData, setCardView, setupDrawer, userDoc, handleAddManyRows, rowLoading } = props;

	// Basically we map out the rows for easy handling, then using the current index and the fields order, force the snap scroll on the children.
	const [rowsMap, setRowsMap] = useState<{ [key: string]: FieldInstance[] }>(() => setupRowsMap());
	const [currentIndex, setCurrentIndex] = useState<number>(0);
	const [rowsHtml, setRowsHtml] = useState<React.JSX.Element[]>([]);
	const [visibleSection, setVisibleSection] = useState(fieldsOrder[currentIndex]);
	const [newlyAddedKey, setNewlyAddedKey] = useState<string | null>(null);
	const swipeHandlers = useSwipe({ onSwipedLeft: () => handleChangeRow(1), onSwipedRight: () => handleChangeRow(-1) });

	const pageSize = 10;

	useEffect(() => {
		const newMap = setupRowsMap();
		setRowsMap(newMap);
	}, [rowsData]);

	useEffect(() => {
		if (rowsMap[initial]) {
			const indexOfRow = fieldsOrder.indexOf(initial);
			setCurrentIndex(indexOfRow);
		}
	}, [initial]);

	useEffect(() => {
		const rowKey = fieldsOrder[currentIndex];
		setVisibleSection(rowKey);
	}, [currentIndex]);

	useEffect(() => {
		renderRows();
	}, [visibleSection]);

	/**
	 * set up a map for the rows, with rowKey: FieldInstance[]
	 */
	function setupRowsMap() {
		const map: { [key: string]: FieldInstance[] } = {};
		for (let i = 0; i < rowsData.length; i++) {
			const row = rowsData[i];
			const rowKey = fieldsOrder[i];
			map[rowKey] = row;
		}
		return map;
	}

	/**
	 * Render the rows then the card shown will switch with the index instead of setting up each row fresh each time.
	 */
	function renderRows() {
		let stuff: Map<string, React.JSX.Element> = new Map<string, React.JSX.Element>();
		const moreStuff = [];
		for (let i = 0; i < rowsData.length; i++) {
			const row = rowsData[i];
			let rowHtml = [];
			let rowKeyArray = [];
			for (let i = 0; i < row.length; i++) {
				const fieldInstance = row[i];
				if (!fieldInstance) continue;
				rowKeyArray.push(fieldInstance.id);
				// to start just loop through and put some field actions with context hooks up in here
				const html = (
					<div key={fieldInstance.id} className={styles.field_wrapper}>
						<UpdateContext.Consumer>
							{({ updateObj, setupChanged }: { updateObj: UpdateObj; setupChanged: Function }) => (
								<FieldAction
									setupDrawer={setupDrawer}
									updateObj={updateObj[fieldInstance.id]}
									fieldInstance={fieldInstance}
									fieldInstanceId={fieldInstance.id}
									fieldTemplate={fieldInstance.fieldTemplate}
									setupChanged={setupChanged}
									online={online}
									userDoc={userDoc}
								/>
							)}
						</UpdateContext.Consumer>
					</div>
				);
				stuff.set(fieldInstance.id, html);
				rowHtml.push(html);
			}

			const rowKey = rowKeyArray.join(",");
			const finalRowHtml = (
				<CardScroll rowKey={rowKey} rowHtml={rowHtml} color={color} visibleSection={visibleSection} isNewlyAdded={rowKey === newlyAddedKey} />
			);
			moreStuff.push(finalRowHtml);
		}
		setRowsHtml(moreStuff);
	}

	function handleChangeRow(direction: number) {
		const newIndex = currentIndex + direction;
		if (newIndex > -1) {
			if (newIndex > rowsHtml.length - 1) {
				setCurrentIndex(currentIndex);
			} else {
				setCurrentIndex(newIndex);
			}
		} else if (newIndex < 0) {
			setCurrentIndex(0);
		}
	}

	function handleSetRowView() {
		setCardView(null);
	}

	async function addCard(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
		const result = await handleAddManyRows(e);
		if (result) {
			setCurrentIndex(fieldsOrder.length);
			setVisibleSection(result.newRowKey);
			setNewlyAddedKey(result.newRowKey);
			setTimeout(() => setNewlyAddedKey(null), 1500);
		}
	}

	return (
		<div className={styles.cardWrapper} style={{ backgroundColor: color.background.primary }}>
			<div className={styles.header_and_actionBar}>
				<div className={styles.header_infoExpanded}>
					<DrawerTutorialButton helpColor={color.text.tertiary} location={"table-card-view"} setupDrawer={setupDrawer} style={{ border: "0px" }} />
					<div className={styles.seperation_line} style={{ background: color.text.secondary }} />
					<div className={styles.tableHeader_buttons}>
						<Button
							colorPalette={color}
							icon={<FiChevronLeft size={36} />}
							onClick={() => handleChangeRow(-1)}
							variant="outlined"
							disabled={rowLoading || currentIndex === 0}
							className={styles.cardNavButtons}
						/>
						<Button
							colorPalette={color}
							icon={<FiChevronRight size={36} />}
							onClick={() => handleChangeRow(1)}
							variant="outlined"
							disabled={rowLoading || currentIndex >= rowsHtml.length - 1}
							className={styles.cardNavButtons}
						/>
						<Button colorPalette={color} onClick={addCard} variant={"outlined"} disabled={rowLoading ? true : false} icon={<FiPlus />}>
							Add Card
						</Button>
						<Button colorPalette={color} onClick={handleSetRowView} variant="outlined" icon={<FiList />}>
							Row View
						</Button>
					</div>
				</div>
			</div>
			<div style={{ backgroundColor: color.background.primary }} id="section-wrapper" className={styles.intersection_list_wrapper} {...swipeHandlers}>
				{paginateCarousel({ items: rowsHtml, pageIndex: Math.ceil((currentIndex + 1) / pageSize), pageSize }).map((row, index) => row)}
			</div>
		</div>
	);
}

export default CardView;
